import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, Lock, Eye, EyeOff, AlertCircle, ArrowRight, Phone, Mail } from "lucide-react";
import { useAuth, homeFor } from "../context/AuthContext";
import { errMsg, BRAND } from "../lib/api";
import { Logo, MotionButton, WhatsAppButton, AuthBackground, DevCredit } from "../components/common";
import { Stagger, Item, EASE, AnimatedProgress } from "../components/motion";

export function FloatField({ id, label, icon: Icon, type = "text", value, onChange, right, testid, autoComplete }) {
  const [focus, setFocus] = useState(false);
  const up = focus || value;
  return (
    <div className={`relative rounded-xl border bg-white transition-[border-color,box-shadow] duration-200 ${focus ? "border-[var(--brand)] shadow-[0_0_0_4px_rgba(227,10,23,0.09)]" : "border-slate-200 hover:border-slate-300"}`}>
      <Icon className={`absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 transition-[color,transform] duration-200 ${focus ? "text-[var(--brand)] scale-110" : "text-slate-400"}`} strokeWidth={1.8} />
      <label htmlFor={id} className={`pointer-events-none absolute left-11 transition-all duration-200 ${up ? "top-2 text-[10.5px] font-semibold text-slate-500" : "top-1/2 -translate-y-1/2 text-sm text-slate-400"}`}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        data-testid={testid}
        className="h-14 w-full rounded-xl bg-transparent pl-11 pr-12 pt-4 text-[15px] text-[var(--ink)] outline-none"
      />
      {right && <div className="absolute right-2 top-1/2 -translate-y-1/2">{right}</div>}
    </div>
  );
}

function BrandSide() {
  return (
    <div className="relative hidden lg:flex flex-col justify-between p-12 xl:p-16">
      <Stagger gap={0.12} delay={0.1}>
        <Item>
          <Logo className="h-28 w-auto" />
        </Item>
        <Item className="mt-14">
          <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-[var(--brand)]">SK Enstitü</p>
        </Item>
        <Item>
          <h1 className="mt-3 max-w-md text-5xl xl:text-6xl font-extrabold leading-[1.02] tracking-tight text-[var(--ink)]">
            Geleceğini Planlayanlar İçin.
          </h1>
        </Item>
        <Item>
          <p className="mt-6 max-w-sm text-base text-slate-500 leading-relaxed">
            <span className="font-bold text-[var(--ink)]">50.000’i aşkın kursiyer,</span> hedeflerine SK Enstitü çatısı altında ilerliyor.
          </p>
        </Item>
      </Stagger>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7, ease: EASE }} className="glass relative mt-10 w-[320px] rounded-2xl p-5">
        <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Dijital Gelişim</p>
          <p className="mt-1 text-sm font-bold text-[var(--ink)]">Eğitim yolculuğunuz tek panelde</p>
          <div className="mt-4 space-y-3">
            {[72, 45, 100].map((v, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full" style={{ background: v === 100 ? "#10b981" : "#e30a17" }} />
                <AnimatedProgress value={v} height="h-1.5" />
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default function Login() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!identifier.trim() || !password) {
      setError("Lütfen giriş bilgilerinizi ve şifrenizi girin.");
      return;
    }
    setState("loading");
    try {
      const u = await login(identifier.trim(), password, remember);
      setState("success");
      setTimeout(() => {
        setUser(u);
        nav(homeFor(u), { replace: true });
      }, 650);
    } catch (err) {
      setState("idle");
      setError(errMsg(err, "Giriş bilgileriniz kontrol edilemedi. Lütfen bilgilerinizi kontrol edin."));
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[var(--canvas)]">
      <AuthBackground />
      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
        <BrandSide />
        <div className="flex flex-col items-center justify-center px-4 py-10 sm:px-8">
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mb-7 flex flex-col items-center text-center lg:hidden">
            <Logo className="h-20 w-auto" />
            <p className="mt-3 text-sm font-semibold text-slate-500">Geleceğini Planlayanlar İçin.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            className="glass gradient-border w-full max-w-[440px] rounded-[22px] p-6 sm:p-9"
            data-testid="login-card"
          >
            <Stagger gap={0.07} delay={0.35}>
              <Item>
                <h2 className="text-[28px] font-extrabold tracking-tight text-[var(--ink)]">Hoş Geldiniz</h2>
              </Item>
              <Item>
                <p className="mt-1.5 text-sm text-slate-500">Eğitimlerinize erişmek için hesabınıza giriş yapın.</p>
              </Item>
              <form onSubmit={submit} className="mt-7 space-y-3.5" noValidate>
                <Item>
                  <FloatField id="identifier" label="E-posta, T.C. Kimlik No veya Telefon" icon={User} value={identifier} onChange={setIdentifier} testid="login-identifier-input" autoComplete="username" />
                </Item>
                <Item>
                  <FloatField
                    id="password"
                    label="Şifre"
                    icon={Lock}
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={setPassword}
                    testid="login-password-input"
                    autoComplete="current-password"
                    right={
                      <button type="button" onClick={() => setShow(!show)} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-[var(--ink)]" data-testid="login-toggle-password-btn" aria-label="Şifreyi göster/gizle">
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    }
                  />
                </Item>
                <Item className="flex items-center justify-between pt-1">
                  <label className="flex cursor-pointer select-none items-center gap-2 text-[13px] text-slate-600">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded accent-[var(--brand)]" data-testid="login-remember-checkbox" />
                    Beni Hatırla
                  </label>
                  <Link to="/sifremi-unuttum" className="link-reveal text-[13px] font-semibold text-[var(--brand)]" data-testid="login-forgot-link">
                    Şifremi Unuttum
                  </Link>
                </Item>
                <AnimatePresence>
                  {error && (
                    <motion.div initial={{ opacity: 0, height: 0, y: -4 }} animate={{ opacity: 1, height: "auto", y: 0 }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="flex items-start gap-2 rounded-xl bg-rose-50 px-3.5 py-3 text-[13px] text-rose-700 ring-1 ring-rose-100" data-testid="login-error">
                        <AlertCircle className="mt-px h-4 w-4 shrink-0" /> {error}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <Item className="pt-2">
                  <MotionButton type="submit" className="w-full h-12 text-[15px]" loading={state === "loading"} success={state === "success"} loadingText="Giriş yapılıyor..." data-testid="login-submit-btn">
                    Giriş Yap <ArrowRight className="h-4 w-4" />
                  </MotionButton>
                </Item>
              </form>
              <Item className="my-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-slate-300">
                <span className="h-px flex-1 bg-slate-200" /> veya <span className="h-px flex-1 bg-slate-200" />
              </Item>
              <Item>
                <WhatsAppButton className="w-full" />
              </Item>
              <Item>
                <p className="mt-5 text-center text-xs leading-relaxed text-slate-400">
                  Hesap bilgileriniz SK Enstitü tarafından iletilir. Destek için{" "}
                  <a href={BRAND.phoneHref} className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-[var(--ink)]" data-testid="login-support-phone"><Phone className="h-3 w-3" />{BRAND.phone}</a>{" "}
                  · <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-[var(--ink)]" data-testid="login-support-email"><Mail className="h-3 w-3" />{BRAND.email}</a>
                </p>
              </Item>
            </Stagger>
          </motion.div>
        </div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.6 }} className="relative z-10 pb-6 lg:absolute lg:bottom-5 lg:left-0 lg:right-0">
        <DevCredit />
      </motion.div>
    </div>
  );
}
